import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { FileBarChart, ArrowLeft, KeyRound, Download, CheckCircle2, MinusCircle, CircleDot, Lock } from "lucide-react";
import { toast } from "sonner";

const CATALOG = [
  "Consolidated Report (GSTR-1 vs 2A vs 3B)", "Full Year GSTR-1 Download (Outward Supply)",
  "Full Year GSTR-1A Download (Outward Supply)", "Full Year GSTR-1 Download (E-Invoices)",
  "Full Year GSTR-2A Download (Inward Supply)", "Full Year GSTR-2B Download (Inward Supply)",
  "Full Year GSTR-3B Download", "Electronic Ledger Report", "E-Way Bill Report (Outward)",
  "E-Way Bill Report (Inward)", "Compliance Report Download", "Tax liabilities and ITC Comparison",
];
const FYS = ["2026-27", "2025-26", "2024-25", "2023-24", "2022-23", "2021-22", "2020-21", "2019-20", "2018-19", "2017-18"];
const GROUP_COLOR = {
  "GSTR-9": "bg-amber-500/10", "GSTR-1": "bg-pink-500/10", "GSTR-1A": "bg-fuchsia-500/10",
  "GSTR-3B": "bg-cyan-500/10", "GSTR-2A": "bg-indigo-500/10", "GSTR-2B": "bg-violet-500/10",
};

function StatusIcon({ status }) {
  if (status === "available") return <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />;
  if (status === "current") return <CircleDot className="h-4 w-4 text-amber-400 mx-auto" />;
  return <MinusCircle className="h-4 w-4 text-slate-500 mx-auto" />;
}

export default function Reports() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [step, setStep] = useState("catalog"); // catalog | fy | report
  const [report, setReport] = useState("");
  const [fy, setFy] = useState("");
  const [matrix, setMatrix] = useState(null);
  const [otpRef, setOtpRef] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); }).catch((e) => toast.error(apiError(e)));
  }, []);

  const loadMatrix = useCallback((selFy) => {
    if (!clientId) return;
    api.get(`/clients/${clientId}/portal/report-matrix?fy=${selFy}`).then((r) => setMatrix(r.data)).catch((e) => toast.error(apiError(e)));
  }, [clientId]);

  const pickReport = (r) => { setReport(r); setStep("fy"); };
  const pickFy = (f) => { setFy(f); setStep("report"); loadMatrix(f); };
  const back = () => { if (step === "report") { setStep("fy"); setMatrix(null); } else if (step === "fy") setStep("catalog"); };

  const requestOtp = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/clients/${clientId}/portal/otp`, {}); setOtpRef(data.otp_ref); toast.success(`OTP sent. ${data.hint || ""}`); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const verifyOtp = async () => {
    setBusy(true);
    try { await api.post(`/clients/${clientId}/portal/verify`, { otp, otp_ref: otpRef }); toast.success("GSTN session active"); setOtp(""); setOtpRef(""); loadMatrix(fy); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const downloadPortal = async (doc) => {
    try {
      const { data } = await api.get(`/clients/${clientId}/portal/download?doc_type=${doc}&period=03${fy.slice(0, 4)}`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a"); a.href = url; a.download = `${doc}-${fy}.json`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${doc}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const downloadXls = (label) => {
    if (!matrix) return;
    const head = ["Tax Return", "Information", ...matrix.months];
    const rows = matrix.rows.map((r) => [r.group, r.information, ...r.months.map((m) => m.status)]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c)}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${label}-${fy}.csv`; a.click(); URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  const clientName = clients.find((c) => c.id === clientId)?.legal_name || "";

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-head text-3xl font-bold tracking-tight flex items-center gap-2">
          <FileBarChart className="h-6 w-6 text-indigo-400" /> Reports
        </h1>
        <div className="flex items-center gap-2 min-w-0 max-w-full">
          <span className="small-label">Client</span>
          <select data-testid="reports-client-select" value={clientId} onChange={(e) => { setClientId(e.target.value); setStep("catalog"); setMatrix(null); }}
            className="min-w-0 max-w-full truncate rounded-md bg-secondary/50 border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500">
            {clients.map((c) => <option key={c.id} value={c.id}>{c.legal_name} · {c.gstin}</option>)}
          </select>
        </div>
      </div>

      {step !== "catalog" && (
        <button data-testid="reports-back" onClick={back} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      )}

      {step === "catalog" && (
        <div className="fade-up">
          <h2 className="font-head text-xl font-semibold">Which report do you want to download?</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-4">Click any one of the choices below.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CATALOG.map((r) => (
              <button key={r} data-testid={`report-choice-${r.slice(0, 12).replace(/\W/g, "")}`} onClick={() => pickReport(r)}
                className="text-left rounded-lg border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 hover:border-indigo-500/50 px-4 py-3 text-sm font-medium transition-colors">
                {r}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === "fy" && (
        <div className="fade-up">
          <h2 className="font-head text-xl font-semibold">For which Financial Year?</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-4">{report}</p>
          <div className="flex flex-wrap gap-3">
            {FYS.map((f) => (
              <button key={f} data-testid={`fy-${f}`} onClick={() => pickFy(f)}
                className="rounded-lg border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 px-4 py-2.5 text-sm font-mono font-semibold transition-colors">
                FY {f}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === "report" && matrix && (
        <div className="fade-up space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="small-label">Consolidated Report</div>
              <h2 className="font-head text-2xl font-bold">GSTR-1/1A vs 2A/2B vs 3B</h2>
              <div className="text-muted-foreground font-mono text-sm">{clientName} · {matrix.gstin} · FY {fy}</div>
            </div>
            <div className="text-right">
              <div className="text-sm mb-2">GSTN Session:{" "}
                <span data-testid="gstn-session-status" className={matrix.connected ? "text-emerald-400" : "text-rose-400"}>{matrix.connected ? "● Active" : "✕ Inactive"}</span>
              </div>
              {!matrix.connected && (
                <div className="flex items-center gap-2 justify-end">
                  <button data-testid="reports-login-otp" onClick={requestOtp} disabled={busy}
                    className="flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-2 text-xs font-medium"><KeyRound className="h-3.5 w-3.5" /> Login using OTP <span className="opacity-70">*Recommended</span></button>
                  <button className="rounded-md border border-border text-muted-foreground px-3 py-2 text-xs" title="Limited to 500 records/report"><Lock className="h-3 w-3 inline mr-1" />Username &amp; Password</button>
                </div>
              )}
              {otpRef && !matrix.connected && (
                <div className="flex items-center gap-2 justify-end mt-2">
                  <input data-testid="reports-otp-input" value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} placeholder="6-digit OTP"
                    className="w-28 rounded-md bg-secondary/50 border border-border px-2 py-1.5 text-xs font-mono tracking-widest focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  <button data-testid="reports-otp-verify" onClick={verifyOtp} disabled={busy} className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 text-xs font-medium">Connect</button>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="font-head font-semibold">STEP 1 — Download data from Government Portal</div>
              <button data-testid="download-gov-portal" onClick={() => matrix.connected ? downloadXls("Consolidated") : toast.error("Login using OTP first")}
                className="flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-3 py-2 text-xs font-medium" disabled={!matrix.connected}>
                <Download className="h-3.5 w-3.5" /> Download data from Government Portal
              </button>
            </div>
            <div className="overflow-x-auto" data-testid="report-matrix-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground border-b border-border">
                    <th className="px-2 py-2 text-left font-mono">Tax Return</th>
                    <th className="px-2 py-2 text-left font-mono">Information</th>
                    {matrix.months.map((m, i) => <th key={i} className="px-1 py-2 font-mono">{m}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((row, i) => (
                    <tr key={i} className={`border-b border-border/40 ${GROUP_COLOR[row.group] || ""}`}>
                      <td className="px-2 py-1.5 font-mono whitespace-nowrap">{row.group}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.information}</td>
                      {row.months.map((m, j) => <td key={j} className="px-1 py-1.5"><StatusIcon status={m.status} /></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-4 mt-3 text-[11px] text-muted-foreground font-mono">
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Available</span>
              <span className="flex items-center gap-1"><CircleDot className="h-3.5 w-3.5 text-amber-400" /> Current period</span>
              <span className="flex items-center gap-1"><MinusCircle className="h-3.5 w-3.5 text-slate-500" /> Not available</span>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="font-head font-semibold mb-1">STEP 2 — Download Consolidated Report</div>
            <p className="text-xs text-muted-foreground mb-4">Party-wise consolidated XLS comparing GSTR-1/1A, GSTR-2A/2B and GSTR-3B.</p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="dl-1a-2a-3b" onClick={() => downloadXls("GSTR1-1A-vs-2A-vs-3B")} className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 text-sm font-semibold">GSTR-1/1A vs 2A vs 3B</button>
              <button data-testid="dl-1a-2b-3b" onClick={() => downloadXls("GSTR1-1A-vs-2B-vs-3B")} className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 text-sm font-semibold">GSTR-1/1A vs 2B vs 3B</button>
              <button data-testid="dl-gstr9" onClick={() => downloadPortal("GSTR3B")} className="rounded-md border border-emerald-500/40 text-emerald-300 px-4 py-2.5 text-sm font-semibold">Filed GSTR-9 XLS</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
